// Axios
import api from './config/api';
import axios from './config/axios';

// Mappers
import partneredLocations from './mappers/partneredLocations';

export default {
    /**
     * Complete the Partner Ship Cart
     * @param { Object } payload
     * @param { Number } payload.userAccountId - User ID
     * @param { Number } payload.websiteId - Location
     * @param { String } payload.jwtToken - Auth
     * @param { Object } payload.completeBody - Information needed to mark complete
     * @param { Number } payload.completeBody.destinationLocationId
     * @param { Number[] } payload.completeBody.serialItemRefNums
     * @param { String } payload.completeBody.shipMethodName
     * @throws 400 Invalid Parameters, 500 Server Error
     */
    postCompletePartnerShip( {
        userAccountId,
        websiteId,
        jwtToken,
        completeBody,
    } ) {
        return axios( {
            url: api.logisticsUrl( `${ websiteId }/${ userAccountId }/partnerShip/complete` ),
            method: 'post',
            headers: api.authHeaders( jwtToken ),
            data: { ...completeBody },
        } );
    },

    /**
     * DEPRECATED: Use updateSerialItemLocator in InventoryManagementApi
     * Physical Simple Scan
     * @param { Object } payload
     * @param { Number } payload.userAccountId - User Id
     * @param { Number } payload.websiteId - Location
     * @param { String } payload.jwtToken - Auth
     * @param { String } payload.ref - Scanned Tag
     * @param { String } payload.locator_name - Location Text
     * @param { String } payload.locator_ref - Scanned Location Tag
     * @throws 400 Invalid Parameters, 403 Forbidden, 404 Item not found, 500 Internal Server Error
     *
     * Well-disguised POST
     */
    updateSerialItemLocator( {
        userAccountId,
        websiteId,
        jwtToken,
        ref,
        locatorName,
        locatorRef,
    } ) {
        return axios( {
            url: api.logisticsUrl( `${ websiteId }/${ userAccountId }/physicalinventory/${ ref }/scan` ),
            method: 'get',
            headers: api.authHeaders( jwtToken ),
            params: {
                locator_name: locatorName,
                locator_ref: locatorRef,
            },
        } );
    },

    /**
     * Validate Serial Item for PartnerShip
     * @param { Object } payload
     * @param { Number } payload.userAccountId - User Id
     * @param { Number } payload.websiteId - Location
     * @param { String } payload.jwtToken - Auth
     * @param { String } payload.code - Scanned Serial Item Code
     * @throws 404 Item not found, 422 Invalid Parameters, 500 Internal Server Error
     */
    getValidateItemForPartnerShip( {
        userAccountId,
        websiteId,
        jwtToken,
        code,
        locationId,
    } ) {
        return axios( {
            url: api.logisticsUrl( `${ websiteId }/${ userAccountId }/${ code }/validate` ),
            method: 'get',
            headers: api.authHeaders( jwtToken ),
            params: {
                locationId,
            },
        } );
    },

    /**
     * DEPRECATED: Use moveInventory in InventoryManagementApi
     * Simple Inventory Move
     * @param { Object } payload
     * @param { Number } payload.userAccountId - User Id
     * @param { Number } payload.websiteId - Location
     * @param { String } payload.jwtToken - Auth
     * @param { String } payload.ref - Item Ref
     * @param { String } payload.new_locator_ref - Location Ref
     * @throws 400 Invalid Parameters, 500 Internal Server Error
     */
    postSimpleInventoryMove( {
        userAccountId,
        websiteId,
        jwtToken,
        ref,
        newLocatorRef,
    } ) {
        return axios( {
            // Remove ID before calling endpoint
            url: api.logisticsUrl( `${ websiteId }/${ userAccountId }/${ ref.slice( 2 ) }/inventory/move` ),
            method: 'post',
            headers: api.authHeaders( jwtToken ),
            params: {
                new_locator_ref: newLocatorRef,
            },
        } );
    },

    /**
     * Get list of all possible partners for shipping
     * @param { Object } payload
     * @param { String } payload.jwtToken - Auth
     * @param { Number } payload.userAccountId - User Id
     * @param { Number } payload.websiteId - a location's websiteId
     * @throws 400 Invalid Parameters, 404 Entity Not Found, 500 Internal Server Error
     */
    async getPartnerList( {
        jwtToken,
        userAccountId,
        websiteId,
    } ) {
        const response = await axios( {
            url: api.logisticsUrl( `${ websiteId }/${ userAccountId }/listPartners` ),
            method: 'get',
            headers: api.authHeaders( jwtToken ),
        } );
        return {
            ...response,
            data: {
                locations: response.data.partnerInfoDtos.map( partneredLocations.inboundPartneredLocations ),
            },
        };
    },

    /**
     * Get list of partnered locations for a given location
     * @param { Object } payload
     * @param { String } payload.jwtToken - Auth
     * @param { Number } payload.userAccountId - User Id
     * @param { Number } payload.websiteId - a location's websiteId
     * @throws 400 Invalid Parameters, 403 Unauthorized, 404 Entity Not Found, 500 Internal Server Error
     */
    async getLocationPartnerList( {
        jwtToken,
        userAccountId,
        websiteId,
    } ) {
        const response = await axios( {
            url: api.logisticsUrl( `${ websiteId }/${ userAccountId }/location/partners` ),
            method: 'get',
            headers: api.authHeaders( jwtToken ),
        } );
        return {
            ...response,
            data: {
                locations: response.data.partnerInfoDtos.map( partneredLocations.inboundPartneredLocations ),
            },
        };
    },

    /**
     * Update a given location's list of partnered locations by websiteId
     * @param { Object } payload
     * @param { String } payload.jwtToken - Auth
     * @param { Number } payload.userAccountId - User Id
     * @param { Number } payload.websiteId - a location's websiteId
     * @param { Number[] } payload.locationIdList - an array of location id's
     * @param { Boolean } payload.filterPartnerShipLocations - true if we want the backend to return a filtered list of partnered locations
     * @throws 400 Invalid Parameters, 500 Internal Server Error
     */
    async updateLocationPartnerList( {
        jwtToken,
        userAccountId,
        websiteId,
        locationIdList,
        filterPartnerShipLocations = true,
    } ) {
        const response = await axios( {
            url: api.logisticsUrl( `${ websiteId }/${ userAccountId }/partnerShip/savePartners` ),
            method: 'post',
            headers: api.authHeaders( jwtToken ),
            data: {
                partnerShipLocationIds: locationIdList,
                filterPartnerShipLocations,
            },
        } );
        return {
            ...response,
            data: {
                locations: response.data.partnerInfoDtos.map( partneredLocations.inboundPartneredLocations ),
            },
        };
    },

    /**
     * Get locator by ref
     * @param { Object } payload
     * @param { Number } payload.userAccountId - User Id
     * @param { Number } payload.websiteId - Location
     * @param { String } payload.jwtToken - Auth
     * @param { Number } payload.locatorRef - Locator Ref
     * @throws 400 Invalid Parameters, 403 Forbidden, 404 Entity Not Found, 500 Internal Server Error
     */
    getShelfLocatorInformation( {
        userAccountId,
        websiteId,
        jwtToken,
        locatorRef,
    } ) {
        return axios( {
            url: api.logisticsUrl( `${ websiteId }/${ userAccountId }/locator/${ locatorRef }` ),
            method: 'get',
            headers: api.authHeaders( jwtToken ),
        } );
    },

    /**
     * DEPRECATED: Use getSerialItems in InventoryManagementApi
     * Get Serial Items for Locator
     * @param { Object } payload
     * @param { Number } payload.userAccountId - User Id
     * @param { Number } payload.websiteId - Location
     * @param { String } payload.jwtToken - Auth
     * @param { Number } payload.locatorRef - Locator Ref
     * @param { boolean } payload.includeListingData - Include Listing Data
     * @throws 400 Invalid Parameters, 403 Forbidden, 404 Entity Not Found, 500 Internal Server Error
     */
    getSerialItemsForLocator( {
        userAccountId,
        websiteId,
        jwtToken,
        locatorRef,
    } ) {
        return axios( {
            url: api.logisticsUrl( `${ websiteId }/${ userAccountId }/locator/${ locatorRef }/serialitems` ),
            method: 'get',
            headers: api.authHeaders( jwtToken ),
            params: {
                // Includes information on whether or not each item has been scanned
                includeListingData: true,
            },
        } );
    },

    /**
     * Serial Item Information
     * @param { Number } payload.userAccountId
     * @param { Number } payload.websiteId
     * @param { String } payload.jwtToken
     * @param { Number } payload.ref
     */
    getSerialItemInfo( {
        userAccountId,
        websiteId,
        jwtToken,
        ref,
    } ) {
        return axios( {
            url: api.logisticsUrl( `${ websiteId }/${ userAccountId }/physicalinventory/${ ref }/info` ),
            method: 'get',
            headers: api.authHeaders( jwtToken ),
        } );
    },
};
